/**
 * QuotationDialog Component
 * Dialog for creating and editing quotations
 * Part codes must be selected from existing categories before entering pricing
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreatePurchaseQuotation, useUpdatePurchaseQuotation, useCreateSalesQuotation, useUpdateSalesQuotation } from '@/hooks/useQuotations';
import { useVendors, useCustomers } from '@/hooks/useVendorCustomer';
import { PricingTierEditor } from './PricingTierEditor';
import { PartCodePickerDialog } from './PartCodePickerDialog';
import type {
  Quotation,
  PurchaseQuotation,
  SalesQuotation,
  QuotationType,
  QuotationItem,
  CreatePurchaseQuotationRequest,
  CreateSalesQuotationRequest,
  UpdatePurchaseQuotationRequest,
  UpdateSalesQuotationRequest,
} from '@/types/quotations';
import { EMPTY_QUOTATION_ITEM } from '@/types/quotations';
import type { IPartCode } from '@/types/psi';
import { CURRENCY_OPTIONS, DEFAULT_CURRENCY } from '@/types/common';

interface QuotationDialogProps {
  type: QuotationType;
  quotation: Quotation | null;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Get vendor_code or customer_code from quotation based on type
 */
function getPartyCodeFromQuotation(quotation: Quotation | null, type: QuotationType): string {
  if (!quotation) return '';
  if (type === 'purchase') {
    return (quotation as PurchaseQuotation).vendor_code || '';
  }
  return (quotation as SalesQuotation).customer_code || '';
}

export const QuotationDialog: React.FC<QuotationDialogProps> = ({
  type,
  quotation,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation('psi');
  const isEditing = !!quotation;

  // Form state
  const [date, setDate] = useState(quotation?.date || new Date().toISOString().split('T')[0]);
  const [partyCode, setPartyCode] = useState(getPartyCodeFromQuotation(quotation, type));
  const [validFrom, setValidFrom] = useState(quotation?.valid_from || new Date().toISOString().split('T')[0]);
  const [validUntil, setValidUntil] = useState(quotation?.valid_until || '');
  const [currency, setCurrency] = useState(quotation?.currency || DEFAULT_CURRENCY);
  const [notes, setNotes] = useState(quotation?.notes || '');
  const [items, setItems] = useState<QuotationItem[]>(
    quotation?.items || []
  );

  // Part code picker state
  const [partCodePickerOpen, setPartCodePickerOpen] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);

  // Data hooks
  const { data: vendors = [] } = useVendors();
  const { data: customers = [] } = useCustomers();

  // Mutation hooks
  const createPurchaseMutation = useCreatePurchaseQuotation();
  const updatePurchaseMutation = useUpdatePurchaseQuotation();
  const createSalesMutation = useCreateSalesQuotation();
  const updateSalesMutation = useUpdateSalesQuotation();

  const isLoading =
    createPurchaseMutation.isLoading ||
    updatePurchaseMutation.isLoading ||
    createSalesMutation.isLoading ||
    updateSalesMutation.isLoading;

  // Item management
  const openPartCodePicker = (index: number | null = null) => {
    setEditingItemIndex(index);
    setPartCodePickerOpen(true);
  };

  const handlePartCodeSelect = (partCode: IPartCode) => {
    const newItem: QuotationItem = {
      ...EMPTY_QUOTATION_ITEM,
      part_code: partCode.part_number,
      description: partCode.vendor_product || '',
      specification: partCode.vendor_spec || '',
      currency,
    };

    if (editingItemIndex !== null && editingItemIndex < items.length) {
      // Replace existing item's part code info
      setItems(items.map((item, i) =>
        i === editingItemIndex
          ? { ...item, part_code: partCode.part_number, description: partCode.vendor_product || '', specification: partCode.vendor_spec || '' }
          : item
      ));
    } else {
      // Add new item
      setItems([...items, newItem]);
    }
    setPartCodePickerOpen(false);
    setEditingItemIndex(null);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, updates: Partial<QuotationItem>) => {
    setItems(items.map((item, i) => (i === index ? { ...item, ...updates } : item)));
  };

  // Form validation
  const validateForm = (): string | null => {
    if (!partyCode) return t('quotations.validation.partyRequired');
    if (!date) return t('quotations.validation.dateRequired');
    if (!validFrom) return t('quotations.validation.validFromRequired');
    if (!validUntil) return t('quotations.validation.validUntilRequired');
    if (items.length === 0) return t('quotations.validation.itemsRequired');

    for (let i = 0; i < items.length; i++) {
      if (!items[i].part_code) {
        return t('quotations.validation.partCodeRequired', { index: i + 1 });
      }
      if (items[i].pricing_tiers.length === 0) {
        return t('quotations.validation.pricingTiersRequired', { index: i + 1 });
      }
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const error = validateForm();
    if (error) {
      alert(error);
      return;
    }

    try {
      if (type === 'purchase') {
        const purchaseData: CreatePurchaseQuotationRequest = {
          date,
          vendor_code: partyCode,
          valid_from: validFrom,
          valid_until: validUntil,
          currency,
          notes,
          items,
        };
        if (isEditing) {
          const updateData: UpdatePurchaseQuotationRequest = purchaseData;
          await updatePurchaseMutation.mutateAsync({ id: quotation!.quotation_id, data: updateData });
        } else {
          await createPurchaseMutation.mutateAsync(purchaseData);
        }
      } else {
        const salesData: CreateSalesQuotationRequest = {
          date,
          customer_code: partyCode,
          valid_from: validFrom,
          valid_until: validUntil,
          currency,
          notes,
          items,
        };
        if (isEditing) {
          const updateData: UpdateSalesQuotationRequest = salesData;
          await updateSalesMutation.mutateAsync({ id: quotation!.quotation_id, data: updateData });
        } else {
          await createSalesMutation.mutateAsync(salesData);
        }
      }
      onSuccess();
    } catch (error) {
      console.error('Failed to save quotation:', error);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {isEditing ? t('quotations.dialog.editTitle') : t('quotations.dialog.createTitle')}
              {' - '}
              {type === 'purchase' ? t('quotations.purchase') : t('quotations.sales')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Basic info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Party selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {type === 'purchase' ? t('quotations.columns.vendor') : t('quotations.columns.customer')} *
                </label>
                <select
                  value={partyCode}
                  onChange={(e) => setPartyCode(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">{t('common.select')}</option>
                  {type === 'purchase'
                    ? vendors.map((vendor) => (
                        <option key={vendor.vendor_code} value={vendor.vendor_code}>
                          {vendor.vendor_name}
                        </option>
                      ))
                    : customers.map((customer) => (
                        <option key={customer.customer_id} value={customer.customer_id}>
                          {customer.chinese_name}
                        </option>
                      ))
                  }
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('quotations.columns.date')} *
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              {/* Currency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('quotations.columns.currency')}
                </label>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {CURRENCY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Valid From */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('quotations.columns.validFrom')} *
                </label>
                <input
                  type="date"
                  value={validFrom}
                  onChange={(e) => setValidFrom(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              {/* Valid Until */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('quotations.columns.validUntil')} *
                </label>
                <input
                  type="date"
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('common.notes')}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>

            {/* Line Items */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-md font-medium text-gray-900 dark:text-gray-100">
                  {t('quotations.dialog.items')}
                </h3>
                <button
                  type="button"
                  onClick={() => openPartCodePicker(null)}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {t('quotations.dialog.addItem')}
                </button>
              </div>

              {/* Empty state */}
              {items.length === 0 && (
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                    {t('quotations.dialog.noItems')}
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {t('quotations.dialog.noItemsHint')}
                  </p>
                  <button
                    type="button"
                    onClick={() => openPartCodePicker(null)}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('quotations.dialog.selectPartCode')}
                  </button>
                </div>
              )}

              {/* Items list */}
              {items.map((item, index) => (
                <div
                  key={index}
                  className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg space-y-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Part Code Header */}
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          {t('quotations.dialog.item')} #{index + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => openPartCodePicker(index)}
                          className="text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
                        >
                          {t('quotations.dialog.changePartCode')}
                        </button>
                      </div>
                      {/* Part Code Display */}
                      <div className="mt-2 flex items-center gap-2">
                        <span className="font-mono font-semibold text-indigo-600 dark:text-indigo-400">
                          {item.part_code}
                        </span>
                        {item.description && (
                          <span className="text-sm text-gray-600 dark:text-gray-300">
                            - {item.description}
                          </span>
                        )}
                      </div>
                      {item.specification && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {item.specification}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="text-red-500 hover:text-red-700 dark:text-red-400 p-1"
                      title={t('common.delete')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>

                  {/* Editable fields */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Description (editable) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('quotations.columns.description')}
                      </label>
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateItem(index, { description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                      />
                    </div>

                    {/* Specification (editable) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('quotations.columns.specification')}
                      </label>
                      <input
                        type="text"
                        value={item.specification}
                        onChange={(e) => updateItem(index, { specification: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                      />
                    </div>

                    {/* Lead Time */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('quotations.columns.leadTime')}
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={item.lead_time_days}
                        onChange={(e) => updateItem(index, { lead_time_days: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                                 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                      />
                    </div>
                  </div>

                  {/* Pricing Tiers */}
                  <PricingTierEditor
                    tiers={item.pricing_tiers}
                    onChange={(tiers) => updateItem(index, { pricing_tiers: tiers })}
                    currency={item.currency || currency}
                  />
                </div>
              ))}
            </div>
          </form>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isLoading || items.length === 0}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? t('common.saving') : (isEditing ? t('common.save') : t('common.create'))}
            </button>
          </div>
        </div>
      </div>

      {/* Part Code Picker Dialog */}
      <PartCodePickerDialog
        isOpen={partCodePickerOpen}
        onClose={() => {
          setPartCodePickerOpen(false);
          setEditingItemIndex(null);
        }}
        onSelect={handlePartCodeSelect}
      />
    </>
  );
};

export default QuotationDialog;
